import { useEffect, useState } from "react";
import { fetchPlaceDetails } from "../../utils/googlePlaces";

// Live Google reviews for the business, fetched in the customer's browser
// (Google's terms don't allow storing review text, so nothing is saved on our
// side; a copy is kept for the browser session only). Shows the best recent
// ones with Google's attribution. Any failure: the section just doesn't show.

const CACHE_MINUTES = 60;

function readCache(placeId) {
  try {
    const raw = sessionStorage.getItem(`bb-reviews-${placeId}`);
    if (!raw) return null;
    const { at, data } = JSON.parse(raw);
    return Date.now() - at < CACHE_MINUTES * 60000 ? data : null;
  } catch {
    return null;
  }
}

function writeCache(placeId, data) {
  try {
    sessionStorage.setItem(`bb-reviews-${placeId}`, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* private mode etc. */
  }
}

export function fetchPlaceReviews(placeId) {
  const cached = readCache(placeId);
  if (cached) return Promise.resolve(cached);
  return fetchPlaceDetails(placeId).then((data) => {
    writeCache(placeId, data);
    return data;
  });
}

export default function GoogleReviews({ placeId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    fetchPlaceReviews(placeId)
      .then((d) => active && setData(d))
      .catch(() => active && setData(null));
    return () => {
      active = false;
    };
  }, [placeId]);

  if (!data) return null;
  const best = data.reviews
    .filter((r) => r.rating >= 4 && (r.text || "").trim().length >= 30)
    .sort((a, b) => (b.time || 0) - (a.time || 0))
    .slice(0, 3);
  if (!best.length && !data.rating) return null;

  return (
    <div className="bg-white border border-birdie-green/15 rounded-2xl p-5 sm:p-6 shadow-sm">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-heading text-xl text-birdie-green">What people say</h2>
        {data.rating && (
          <span className="text-sm text-birdie-charcoal/70 whitespace-nowrap">
            <span className="text-birdie-mustard">★</span> <strong>{Number(data.rating).toFixed(1)}</strong>
            {data.total ? ` · ${data.total} reviews` : ""}
          </span>
        )}
      </div>
      <div className="mt-3 space-y-3">
        {best.map((r, i) => (
          <figure key={i} className="rounded-xl bg-birdie-cream/70 p-3">
            <div className="text-birdie-mustard text-sm" aria-label={`${r.rating} out of 5 stars`}>
              {"★".repeat(r.rating)}
              <span className="text-birdie-charcoal/20">{"★".repeat(5 - r.rating)}</span>
            </div>
            <blockquote className="text-sm text-birdie-charcoal/80 mt-1 line-clamp-5">{r.text}</blockquote>
            <figcaption className="text-xs text-birdie-charcoal/50 mt-1.5">
              {r.author} · {r.when}
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="mt-3 flex justify-between items-center text-xs text-birdie-charcoal/50">
        <span>Reviews from Google</span>
        {data.url && (
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-birdie-green">
            See all on Google →
          </a>
        )}
      </div>
    </div>
  );
}
